import { AppEnvMap } from '@/core/app-env';
import { SkylineErrorResponse } from '@/services/types';
import { ServiceErrorFactory } from '@/services/utils/service-error-factory';
import { getCookie } from '@/utils/horizon-cookies.client';

type Method = 'GET' | 'POST' | 'PATCH' | 'PUT' | 'DELETE';

type Primitive = string | number | boolean;

export interface SkyOptions {
  query?: Record<string, Primitive | undefined | null>;
  headers?: Record<string, string>;
  /** Defaults to true. Set false to omit the x-csrf-token header. */
  csrf?: boolean;
  signal?: AbortSignal;
}

class SkylineClient {
  constructor(private readonly baseUrl: string) {}

  get<T>(path: string, opts?: SkyOptions): Promise<T> {
    return this.request<T>('GET', path, undefined, opts);
  }
  post<T>(path: string, body?: unknown, opts?: SkyOptions): Promise<T> {
    return this.request<T>('POST', path, body, opts);
  }
  patch<T>(path: string, body?: unknown, opts?: SkyOptions): Promise<T> {
    return this.request<T>('PATCH', path, body, opts);
  }
  put<T>(path: string, body?: unknown, opts?: SkyOptions): Promise<T> {
    return this.request<T>('PUT', path, body, opts);
  }
  delete<T>(path: string, body?: unknown, opts?: SkyOptions): Promise<T> {
    return this.request<T>('DELETE', path, body, opts);
  }

  private async request<T>(
    method: Method,
    path: string,
    body: unknown,
    opts: SkyOptions = {},
  ): Promise<T> {
    const fnName = `${method} ${path}`;
    const url = this.buildUrl(path, opts.query);

    const isFormData = body instanceof FormData;
    const headers: Record<string, string> = {
      Accept: 'application/json',
      ...(opts.headers ?? {}),
    };
    if (body !== undefined && !isFormData && !headers['Content-Type']) {
      headers['Content-Type'] = 'application/json';
    }
    if (opts.csrf !== false) {
      const csrf = getCookie('CSRF');
      if (csrf) headers['x-csrf-token'] = csrf;
    }

    let response: Response;
    try {
      response = await fetch(url, {
        method,
        headers,
        credentials: 'include',
        signal: opts.signal,
        body:
          body === undefined
            ? undefined
            : isFormData
              ? body
              : JSON.stringify(body),
      });
    } catch (err) {
      throw ServiceErrorFactory.createNew(fnName, err as Error);
    }

    if (!response.ok) {
      let errBody: SkylineErrorResponse | Error;
      try {
        errBody = (await response.json()) as SkylineErrorResponse;
      } catch {
        errBody = new Error(`HTTP ${response.status} ${response.statusText}`);
      }
      throw ServiceErrorFactory.createNew(fnName, errBody);
    }

    if (response.status === 204) return undefined as T;

    const contentType = response.headers.get('content-type') ?? '';
    if (contentType.includes('application/json')) {
      return (await response.json()) as T;
    }
    return (await response.text()) as unknown as T;
  }

  private buildUrl(
    path: string,
    query?: Record<string, Primitive | undefined | null>,
  ): string {
    const url = new URL(`${this.baseUrl}${path}`);
    if (query) {
      for (const [k, v] of Object.entries(query)) {
        if (v !== undefined && v !== null) url.searchParams.set(k, String(v));
      }
    }
    return url.toString();
  }
}

export const sky = new SkylineClient(AppEnvMap.BaseUrl);
